import _ from 'lodash';
import { DeepMap, FieldErrors } from 'react-hook-form';
import { EmptyObject } from 'shared/utils/type-utils';
import useErnie from 'shared/hooks/use-ernie';
import { GqlDispensaries as Dispensary } from 'types/graphql';

/**
 * Whether a specific (possibly nested, dot-delimited) field path is dirty, given
 * react-hook-form's `formState.dirtyFields`. Works for leaves (boolean flags) and
 * subtrees (returns true when the subtree has any dirty entries). Used to scope the
 * "copy to other locations" affordance's isDirty prop to a single setting rather
 * than the whole form.
 */
export function isFieldDirty(dirtyFields: unknown, path: string): boolean {
  const current: unknown = _.get(dirtyFields, path);
  if (current == null) {
    return false;
  }
  if (typeof current === 'boolean') {
    return current;
  }
  if (typeof current === 'object') {
    return Object.keys(current).length > 0;
  }
  return Boolean(current);
}

// lots of type assertions here unfortunately. I'd love for this function
// to be rewritten in a better way. Pushing forward to alleviate dispensary issues.
export function getDirtyFormValues<ValuesType>(
  dirtyFields: DeepMap<ValuesType, true>,
  sourceData: ValuesType
): ValuesType {
  const finalData = {} as ValuesType;
  const objKeys = Object.keys(dirtyFields) as Array<keyof ValuesType>;

  objKeys.forEach((key) => {
    const value = dirtyFields[key] as DeepMap<ValuesType[keyof ValuesType], true>;

    if (value.constructor.name === 'Object') {
      finalData[key] = getDirtyFormValues<ValuesType[keyof ValuesType]>(value, sourceData[key]);
    } else {
      finalData[key] = sourceData[key];
    }
  });

  return finalData;
}
export function nullToUndefined<T>(_castValue: T, originalValue: T | null): T | undefined {
  return originalValue === null ? undefined : originalValue;
}

export function NaNToUndefined<T>(_castValue: T, originalValue: T | number | null): number | undefined {
  return Number.isNaN(originalValue) ? undefined : Number(originalValue);
}

export function removeNullValues(obj: Dispensary | EmptyObject): Partial<Dispensary> {
  return (
    Object.entries(obj)
      // Filter out all null values from the object entries array

      .filter(([_key, value]) => value != null)
      // Check if the value is an object, if it is we will recursively pass that value back through to be evaluated.
      // Arrays are handled separately (map, not Object.entries+reduce) so an array-of-objects
      // field (e.g. storeSettings.priceRangeFilter.bands) stays an array instead of being
      // rebuilt into a plain object keyed "0"/"1"/"2" -- which then fails yup's `array()` cast
      // with a real (uncaught, render-crashing) TypeError since the value is no longer an array.
      .reduce((acc, [key, value]) => {
        let normalizedValue = value;
        if (Array.isArray(value)) {
          normalizedValue = value.map((item) => (item === Object(item) ? removeNullValues(item) : item));
        } else if (value === Object(value)) {
          normalizedValue = removeNullValues(value);
        }
        return {
          ...acc,
          [key]: normalizedValue,
        };
      }, {})
  );
}

export type FirstFormError = {
  message: string;
  path: string;
};
export type FormErrorDetails = FirstFormError & {
  type?: string;
};

const collectFormErrors = (value: unknown, path: string[]): FormErrorDetails[] => {
  if (!value || typeof value !== 'object') return [];
  if ('message' in value && typeof value.message === 'string') {
    return [
      {
        message: value.message,
        path: path.join('.') || 'root',
        ...('type' in value && typeof value.type === 'string' ? { type: value.type } : {}),
      },
    ];
  }

  const errorRecord = value as Record<string, unknown>;
  const childKeys = Object.keys(errorRecord)
    .filter((key) => key !== 'root')
    .sort((left, right) => {
      const leftIndex = Number(left);
      const rightIndex = Number(right);
      if (Number.isInteger(leftIndex) && Number.isInteger(rightIndex)) return leftIndex - rightIndex;
      return 0;
    });
  const childErrors = childKeys.flatMap((key) => collectFormErrors(errorRecord[key], [...path, key]));
  return [...childErrors, ...collectFormErrors(errorRecord.root, path)];
};

export function getFormErrors(errors: unknown): FormErrorDetails[] {
  return collectFormErrors(errors, []);
}

export function getFirstFormError(errors: unknown): FirstFormError | null {
  const formErrors = getFormErrors(errors);
  if (formErrors.length === 0) return null;
  return { message: formErrors[0].message, path: formErrors[0].path };
}

type UseErnieOnSubmitFormErrorsReturn = {
  handleFormErrors: (errors: FieldErrors) => void;
};

export function useErnieOnSubmitFormErrors(): UseErnieOnSubmitFormErrorsReturn {
  const showErnie = useErnie();

  function handleFormErrors(errors: FieldErrors): void {
    const error = getFirstFormError(errors);
    showErnie(error?.message ?? 'Please review the highlighted fields.', 'danger');
  }

  return { handleFormErrors };
}
